import { type ReactNode, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { Pagination } from '@/components/ui/pagination'
import { type SalesLeadActivity } from '@/models/salesLead'
import { ActivityAnchor } from './ActivityAnchor'

const ACTIVITIES_PER_PAGE = 20

/** The page holding the activity a `#activity-<id>` deep link targets, or null when it isn't here. */
function deepLinkedPage(activities: SalesLeadActivity[], hash: string): number | null {
  const index = activities.findIndex((activity) => hash === `#activity-${activity.id}`)
  return index === -1 ? null : Math.floor(index / ACTIVITIES_PER_PAGE) + 1
}

interface ActivityPagesProps {
  activities: SalesLeadActivity[]
  renderActivity: (activity: SalesLeadActivity) => ReactNode
}

/** A timeline one page at a time. A `#activity-<id>` deep link opens on the page holding it. */
export function ActivityPages({ activities, renderActivity }: ActivityPagesProps) {
  const { hash } = useLocation()
  const listRef = useRef<HTMLDivElement>(null)
  const [requestedPage, setRequestedPage] = useState(() => deepLinkedPage(activities, hash) ?? 1)
  const [handledHash, setHandledHash] = useState(hash)
  // A deep link followed while the timeline is open, e.g. from global search.
  if (hash !== handledHash) {
    setHandledHash(hash)
    setRequestedPage((current) => deepLinkedPage(activities, hash) ?? current)
  }

  // Clamped rather than stored, so a shrinking timeline can't strand us on an empty page.
  const totalPages = Math.max(1, Math.ceil(activities.length / ACTIVITIES_PER_PAGE))
  const page = Math.min(requestedPage, totalPages)
  const firstIndex = (page - 1) * ACTIVITIES_PER_PAGE
  const endIndex = Math.min(firstIndex + ACTIVITIES_PER_PAGE, activities.length)

  function goToPage(next: number) {
    setRequestedPage(next)
    // The pager sits below a long list: start the new page from its top.
    if (listRef.current && listRef.current.getBoundingClientRect().top < 0) {
      listRef.current.scrollIntoView({ block: 'start' })
    }
  }

  return (
    <>
      <div ref={listRef} className="flex scroll-mt-24 flex-col gap-3">
        {activities.map((activity, index) => (
          // Off-page activities stay mounted, so an unsent reply or note edit survives paging.
          <div key={activity.id} hidden={index < firstIndex || index >= endIndex}>
            <ActivityAnchor activityId={activity.id}>{renderActivity(activity)}</ActivityAnchor>
          </div>
        ))}
      </div>
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={goToPage}
        summary={`${firstIndex + 1}–${endIndex} of ${activities.length} activities`}
      />
    </>
  )
}
